/**
 * LegalDocumentsPage - Admin Panel
 * Il Diario di Bordo - SaaS
 * 
 * Pagina per gestione completa documenti legali:
 * - Lista documenti con filtri
 * - CRUD documenti
 * - Editor Markdown/WYSIWYG
 * - Upload PDF
 * - Dashboard firme
 * - Richiesta ri-firma
 * 
 * @version 1.0.0
 * @created Gennaio 2025
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { adminApi } from '../services/api'
import ReactMarkdown from 'react-markdown'
import {
  FileText,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Archive,
  Send,
  Upload,
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Loader2,
  FileCheck,
  Users,
  Calendar,
  Bell,
  RefreshCw,
  ExternalLink,
  Copy,
  Save,
  X
} from 'lucide-react'

// ============================================
// COSTANTI
// ============================================

const DOCUMENT_TYPES = [
  { value: 'tos', label: 'Termini di Servizio', icon: '📋' },
  { value: 'privacy', label: 'Informativa Privacy', icon: '🔒' },
  { value: 'dpa', label: 'Data Processing Agreement', icon: '📝' },
  { value: 'cookie', label: 'Cookie Policy', icon: '🍪' },
  { value: 'conduct_code', label: 'Codice di Condotta', icon: '👨‍🏫' },
  { value: 'liability_waiver', label: 'Liberatoria Responsabilità', icon: '⚠️' },
  { value: 'insurance', label: 'Polizza Assicurativa', icon: '🛡️' },
  { value: 'parental_consent_template', label: 'Modello Consenso Genitori', icon: '👨‍👩‍👧' },
  { value: 'other', label: 'Altro', icon: '📄' }
]

const TARGET_AUDIENCES = [
  { value: 'responsabile', label: 'Solo Responsabili' },
  { value: 'coordinatore', label: 'Solo Coordinatori' },
  { value: 'educatore', label: 'Solo Educatori' },
  { value: 'tenant_users', label: 'Coordinatori + Educatori' },
  { value: 'org_users', label: 'Tutti (Resp + Coord + Educ)' },
  { value: 'all', label: 'Tutti gli utenti' }
]

const BLOCK_TYPES = [
  { value: 'hard', label: 'Blocco Hard', description: 'Non può accedere a nulla', color: 'red', icon: '🔴' },
  { value: 'soft', label: 'Blocco Soft', description: 'Banner persistente', color: 'yellow', icon: '🟡' },
  { value: 'none', label: 'Nessun Blocco', description: 'Documento informativo', color: 'green', icon: '🟢' }
]

const ACCEPTANCE_TYPES = [
  { value: 'checkbox', label: 'Checkbox "Accetto"' },
  { value: 'signature', label: 'Firma Digitale' },
  { value: 'view_only', label: 'Solo Visualizzazione' }
]

const CONTENT_SOURCES = [
  { value: 'editor', label: 'Editor Markdown', icon: '✏️' },
  { value: 'external_url', label: 'URL Esterno', icon: '🔗' },
  { value: 'pdf', label: 'File PDF', icon: '📄' }
]

// ============================================
// COMPONENTE LISTA DOCUMENTI
// ============================================

function DocumentsList() {
  const navigate = useNavigate()
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusCounts, setStatusCounts] = useState({ draft: 0, active: 0, archived: 0 })
  const [nonCompliantCount, setNonCompliantCount] = useState(0)

  // Filtri (statusFilter accetta anche il valore 'non-compliant' per la vista trasversale)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({ total: 0, pages: 1 })

  const loadDocuments = useCallback(async () => {
    // La vista trasversale non conformi ha il proprio loader
    if (statusFilter === 'non-compliant') {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const params = { page, limit: 20 }
      if (statusFilter) params.status = statusFilter
      if (typeFilter) params.type = typeFilter

      const response = await adminApi.getLegalDocuments(params)

      let docs = response.data.data.documents || []

      // Filtro client-side per search
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        docs = docs.filter(d =>
          d.title?.toLowerCase().includes(query) ||
          d.type?.toLowerCase().includes(query)
        )
      }

      setDocuments(docs)
      setStatusCounts(response.data.data.statusCounts || { draft: 0, active: 0, archived: 0 })
      setPagination(response.data.data.pagination || { total: 0, pages: 1 })
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, typeFilter, searchQuery])

  // Carica counter non conformi (refreshato a ogni cambio tab)
  const loadNonCompliantCount = useCallback(async () => {
    try {
      const res = await adminApi.getNonCompliantOrganizations({ countOnly: true })
      setNonCompliantCount(res.data?.data?.count || 0)
    } catch {
      // Silenzioso — il counter non è critico
      setNonCompliantCount(0)
    }
  }, [])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  useEffect(() => {
    loadNonCompliantCount()
  }, [loadNonCompliantCount, statusFilter])

  const handleDelete = async (doc) => {
    if (doc.status !== 'draft') {
      alert('Solo i documenti in bozza possono essere eliminati. Usa "Archivia" invece.')
      return
    }
    
    if (!confirm(`Eliminare "${doc.title}"? Questa azione non può essere annullata.`)) {
      return
    }
    
    try {
      await adminApi.deleteLegalDocument(doc._id)
      loadDocuments()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore nell\'eliminazione')
    }
  }

  const handleArchive = async (doc) => {
    if (!confirm(`Archiviare "${doc.title}"?`)) return
    
    try {
      await adminApi.archiveLegalDocument(doc._id)
      loadDocuments()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore nell\'archiviazione')
    }
  }

  const handlePublish = async (doc) => {
    navigate(`/legal-documents/${doc._id}/publish`)
  }

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-gray-100 text-gray-700', icon: <Edit className="h-3 w-3" />, label: 'Bozza' },
      active: { color: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3 w-3" />, label: 'Attivo' },
      archived: { color: 'bg-orange-100 text-orange-700', icon: <Archive className="h-3 w-3" />, label: 'Archiviato' }
    }
    const badge = badges[status] || badges.draft
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.icon} {badge.label}
      </span>
    )
  }

  const getBlockBadge = (blockType) => {
    const block = BLOCK_TYPES.find(b => b.value === blockType)
    if (!block) return null
    return (
      <span className="text-xs">
        {block.icon} {block.label}
      </span>
    )
  }

  const getTypeInfo = (type) => {
    return DOCUMENT_TYPES.find(t => t.value === type) || { icon: '📄', label: type }
  }

  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documenti Legali</h1>
          <p className="text-gray-500">Gestisci termini, privacy, DPA e altri documenti</p>
        </div>
        <button
          onClick={() => navigate('/legal-documents/new')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nuovo Documento
        </button>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-2 border-b">
        {[
          { value: '', label: 'Tutti', count: statusCounts.draft + statusCounts.active + statusCounts.archived },
          { value: 'active', label: 'Attivi', count: statusCounts.active },
          { value: 'draft', label: 'Bozze', count: statusCounts.draft },
          { value: 'archived', label: 'Archiviati', count: statusCounts.archived },
          { value: 'non-compliant', label: 'Non conformi', count: nonCompliantCount, danger: true }
        ].map(tab => (
          <button
            key={tab.value || 'all'}
            onClick={() => { setStatusFilter(tab.value); setPage(1) }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              statusFilter === tab.value
                ? (tab.danger ? 'border-red-600 text-red-600' : 'border-primary-600 text-primary-600')
                : (tab.danger && tab.count > 0
                    ? 'border-transparent text-red-600 hover:text-red-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700')
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Vista Non Conformi */}
      {statusFilter === 'non-compliant' && (
        <NonCompliantList onRefresh={loadNonCompliantCount} />
      )}

      {/* Filters (nascosti in vista Non conformi) */}
      {statusFilter !== 'non-compliant' && (
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca documenti..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Tutti i tipi</option>
          {DOCUMENT_TYPES.map(type => (
            <option key={type.value} value={type.value}>
              {type.icon} {type.label}
            </option>
          ))}
        </select>

        <button
          onClick={loadDocuments}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </button>
      </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Documents List + Pagination (nascosti in vista Non conformi) */}
      {statusFilter !== 'non-compliant' && (
      <>
      <div className="space-y-4">
        {documents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nessun documento trovato</p>
            <button
              onClick={() => navigate('/legal-documents/new')}
              className="mt-4 text-primary-600 hover:underline"
            >
              Crea il primo documento
            </button>
          </div>
        ) : (
          documents.map(doc => {
            const typeInfo = getTypeInfo(doc.type)
            return (
              <div
                key={doc._id}
                className="bg-white border rounded-xl p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{typeInfo.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                        <p className="text-sm text-gray-500">
                          {typeInfo.label} • v{doc.version}
                        </p>
                      </div>
                      {getStatusBadge(doc.status)}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-3">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {TARGET_AUDIENCES.find(a => a.value === doc.targetAudience)?.label || doc.targetAudience}
                      </span>
                      {getBlockBadge(doc.blockType)}
                      {doc.signatureDeadline && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Deadline: {new Date(doc.signatureDeadline).toLocaleDateString('it-IT')}
                        </span>
                      )}
                    </div>
                    
                    {doc.status === 'active' && doc.signatureStats && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                doc.signatureStats.percentage === 100 ? 'bg-green-500' :
                                doc.signatureStats.percentage >= 80 ? 'bg-blue-500' :
                                doc.signatureStats.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${doc.signatureStats.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">
                            {doc.signatureStats.accepted}/{doc.signatureStats.required} ({doc.signatureStats.percentage}%)
                          </span>
                          {doc.signatureStats.percentage === 100 && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/legal-documents/${doc._id}`)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                      title="Visualizza"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    
                    <button
                      onClick={() => navigate(`/legal-documents/${doc._id}/edit`)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                      title="Modifica"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    
                    {doc.status === 'active' && (
                      <button
                        onClick={() => navigate(`/legal-documents/${doc._id}/signatures`)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                        title="Dashboard Firme"
                      >
                        <FileCheck className="h-5 w-5" />
                      </button>
                    )}
                    
                    {doc.status === 'draft' && (
                      <button
                        onClick={() => handlePublish(doc)}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                        title="Pubblica"
                      >
                        <Send className="h-5 w-5" />
                      </button>
                    )}
                    
                    {doc.status === 'active' && (
                      <button
                        onClick={() => handleArchive(doc)}
                        className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                        title="Archivia"
                      >
                        <Archive className="h-5 w-5" />
                      </button>
                    )}
                    
                    {doc.status === 'draft' && (
                      <button
                        onClick={() => handleDelete(doc)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Elimina"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Pagina {page} di {pagination.pages} ({pagination.total} documenti)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              disabled={page === pagination.pages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  )
}

// ============================================
// COMPONENTE NON CONFORMI (vista trasversale per organization)
// ============================================

function NonCompliantList({ onRefresh }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionMsg, setActionMsg] = useState(null)
  const [modal, setModal] = useState(null) // { type: 'extend'|'exempt'|'reminder', org, document }

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminApi.getNonCompliantOrganizations()
      setRows(res.data?.data?.organizations || [])
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const refreshAll = () => {
    load()
    onRefresh?.()
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—'

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-red-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <p className="text-red-700">{error}</p>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-12 bg-green-50 rounded-lg border border-green-200">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <p className="text-green-700 font-medium">Nessuna organization non conforme</p>
        <p className="text-sm text-green-600 mt-1">Tutte le organization hanno firmato i documenti required.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {actionMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <p className="text-green-700 text-sm">{actionMsg}</p>
        </div>
      )}

      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-red-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-red-700">Organization</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-red-700">Documenti mancanti</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-red-700">Ritardo (max)</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-red-700">Ultimo reminder</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-red-700">Stato</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map(row => (
              <tr key={row.organizationId} className="hover:bg-gray-50">
                <td className="px-4 py-3 align-top">
                  <p className="font-medium text-gray-900">{row.organizationName}</p>
                  {row.responsabile && (
                    <p className="text-xs text-gray-500">
                      {row.responsabile.nome} {row.responsabile.cognome} · {row.responsabile.email}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <ul className="space-y-2">
                    {row.missingDocuments.map(d => (
                      <li key={d.documentId} className="text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{d.title}</span>
                          <span className="text-gray-500">v{d.version}</span>
                          {d.blockType === 'hard' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">hard</span>
                          )}
                          {d.blockType === 'soft' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">soft</span>
                          )}
                          <span className="ml-auto flex gap-1">
                            <button
                              onClick={() => setModal({ type: 'extend', org: row, document: d })}
                              className="px-2 py-0.5 text-xs border border-blue-500 text-blue-600 rounded hover:bg-blue-50"
                              title={`Estendi deadline per "${d.title}"`}
                            >
                              Estendi
                            </button>
                            <button
                              onClick={() => setModal({ type: 'exempt', org: row, document: d })}
                              className="px-2 py-0.5 text-xs border border-gray-400 text-gray-600 rounded hover:bg-gray-50"
                              title={`Esonera da "${d.title}"`}
                            >
                              Esonera
                            </button>
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </td>
                <td className="px-4 py-3 align-top text-sm">
                  <span className="font-medium text-red-600">{row.daysOverdue} gg</span>
                </td>
                <td className="px-4 py-3 align-top text-sm text-gray-500">
                  {formatDate(row.lastReminderAt)}
                </td>
                <td className="px-4 py-3 align-top">
                  {row.isReadOnly ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      <XCircle className="h-3 w-3" /> Sola-lettura
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                      <Clock className="h-3 w-3" /> In attesa
                    </span>
                  )}
                  {row.exceptions?.length > 0 && (
                    <p className="text-xs text-blue-600 mt-1">{row.exceptions.length} eccezione/i attive</p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal estendi/esonera */}
      {modal && (
        <ComplianceActionModal
          modal={modal}
          onClose={() => setModal(null)}
          onSuccess={(msg) => {
            setModal(null)
            setActionMsg(msg)
            setTimeout(() => setActionMsg(null), 5000)
            refreshAll()
          }}
        />
      )}
    </div>
  )
}

// Modal per estendi/esonera
function ComplianceActionModal({ modal, onClose, onSuccess }) {
  const isExtend = modal.type === 'extend'
  const [extendedUntil, setExtendedUntil] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 30)
    return d.toISOString().slice(0, 10)
  })
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      if (isExtend) {
        await adminApi.extendComplianceDeadline(modal.org.organizationId, {
          documentId: modal.document.documentId,
          extendedUntil: new Date(extendedUntil).toISOString(),
          reason: reason || undefined
        })
        onSuccess('Estensione deadline registrata')
      } else {
        if (reason.trim().length < 5) {
          setErr('Il motivo è obbligatorio (min 5 caratteri)')
          setSubmitting(false)
          return
        }
        await adminApi.grantComplianceExemption(modal.org.organizationId, {
          documentId: modal.document.documentId,
          reason
        })
        onSuccess('Esonero concesso')
      }
    } catch (e2) {
      setErr(e2.response?.data?.message || 'Errore durante l\'operazione')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {isExtend ? 'Estendi deadline' : 'Concedi esonero permanente'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {modal.org.organizationName} · {modal.document.title} v{modal.document.version}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isExtend && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nuova deadline</label>
              <input
                type="date"
                value={extendedUntil}
                onChange={(e) => setExtendedUntil(e.target.value)}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                required
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo {isExtend ? '(opzionale)' : '(obbligatorio, min 5 caratteri)'}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              required={!isExtend}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder={isExtend ? 'Es. richiesta del cliente, vacanze...' : 'Es. accordo separato, contratto enterprise...'}
            />
          </div>
          {err && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{err}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 ${
                isExtend ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-700 hover:bg-gray-800'
              } disabled:opacity-50`}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isExtend ? 'Concedi estensione' : 'Concedi esonero'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================
// COMPONENTE FORM DOCUMENTO
// ============================================

function DocumentForm({ isEdit = false }) {
  const navigate = useNavigate()
  const { id } = useParams()
  
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [previewMode, setPreviewMode] = useState(false)
  
  const [form, setForm] = useState({
    type: 'tos',
    title: '',
    description: '',
    version: '1.0',
    content: {
      body: '',
      externalUrl: '',
      summary: ''
    },
    contentSource: 'editor',
    targetAudience: 'responsabile',
    required: true,
    requiresFullRead: false,
    acceptanceType: 'checkbox',
    blockType: 'hard',
    effectiveDate: new Date().toISOString().split('T')[0],
    signatureDeadline: '',
    notifications: {
      onPublish: true,
      reminderDays: [7, 3, 1, 0]
    },
    status: 'draft'
  })
  
  const [pdf, setPdf] = useState(null)

  useEffect(() => {
    if (isEdit && id) {
      loadDocument()
    }
  }, [isEdit, id])

  const loadDocument = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getLegalDocument(id)
      const doc = response.data.data.document
      
      setForm({
        ...doc,
        effectiveDate: doc.effectiveDate?.split('T')[0] || '',
        signatureDeadline: doc.signatureDeadline?.split('T')[0] || '',
        content: doc.content || { body: '', externalUrl: '', summary: '' },
        notifications: doc.notifications || { onPublish: true, reminderDays: [7, 3, 1, 0] }
      })
      
      if (doc.pdf) {
        setPdf(doc.pdf)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!form.title || !form.version) {
      setError('Titolo e versione sono obbligatori')
      return
    }
    
    try {
      setSaving(true)
      setError(null)
      
      const data = {
        ...form,
        signatureDeadline: form.signatureDeadline || null
      }
      
      if (isEdit) {
        await adminApi.updateLegalDocument(id, data)
      } else {
        await adminApi.createLegalDocument(data)
      }
      
      navigate('/legal-documents')
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    if (file.type !== 'application/pdf') {
      setError('Solo file PDF sono permessi')
      return
    }
    
    if (file.size > 10 * 1024 * 1024) {
      setError('Il file non può superare 10MB')
      return
    }
    
    if (!id) {
      setError('Salva prima il documento, poi potrai caricare il PDF')
      return
    }
    
    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('pdf', file)
      
      const response = await adminApi.uploadLegalDocumentPdf(id, formData)
      setPdf(response.data.data.pdf)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento PDF')
    } finally {
      setUploading(false)
    }
  }

  const handleRemovePdf = async () => {
    if (!confirm('Rimuovere il PDF?')) return
    
    try {
      await adminApi.deleteLegalDocumentPdf(id)
      setPdf(null)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella rimozione PDF')
    }
  }

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const updateContent = (field, value) => {
    setForm(prev => ({
      ...prev,
      content: { ...prev.content, [field]: value }
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/legal-documents')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Modifica Documento' : 'Nuovo Documento'}
          </h1>
          <p className="text-gray-500">
            {isEdit ? `Modifica "${form.title}"` : 'Crea un nuovo documento legale'}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4 text-red-500" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informazioni Base */}
        <div className="bg-white border rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">Informazioni Base</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => updateForm('title', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Es: Termini di Servizio"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Versione *
              </label>
              <input
                type="text"
                value={form.version}
                onChange={(e) => updateForm('version', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Es: 1.0"
                pattern="\d+\.\d+"
                title="Formato: X.Y (es: 1.0, 2.1)"
                required
                disabled={isEdit}
              />
              {isEdit && (
                <p className="text-xs text-gray-500 mt-1">
                  Per cambiare versione, crea una nuova versione
                </p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo Documento *
              </label>
              <select
                value={form.type}
                onChange={(e) => updateForm('type', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                disabled={isEdit}
              >
                {DOCUMENT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chi deve firmare *
              </label>
              <select
                value={form.targetAudience}
                onChange={(e) => updateForm('targetAudience', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {TARGET_AUDIENCES.map(aud => (
                  <option key={aud.value} value={aud.value}>
                    {aud.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrizione breve
            </label>
            <textarea
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={2}
              maxLength={500}
              placeholder="Breve descrizione del documento..."
            />
          </div>
        </div>

        {/* Obbligatorietà e Blocco */}
        <div className="bg-white border rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">Obbligatorietà e Comportamento</h2>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(e) => updateForm('required', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm">Documento obbligatorio</span>
            </label>
            
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.requiresFullRead}
                onChange={(e) => updateForm('requiresFullRead', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm">Richiede scroll completo prima di accettare</span>
            </label>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo di accettazione
              </label>
              <div className="flex gap-4">
                {ACCEPTANCE_TYPES.map(acc => (
                  <label key={acc.value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="acceptanceType"
                      value={acc.value}
                      checked={form.acceptanceType === acc.value}
                      onChange={(e) => updateForm('acceptanceType', e.target.value)}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm">{acc.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comportamento se non firmato
              </label>
              <div className="grid grid-cols-3 gap-3">
                {BLOCK_TYPES.map(block => (
                  <label
                    key={block.value}
                    className={`relative flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      form.blockType === block.value
                        ? `border-${block.color}-500 bg-${block.color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="blockType"
                      value={block.value}
                      checked={form.blockType === block.value}
                      onChange={(e) => updateForm('blockType', e.target.value)}
                      className="sr-only"
                    />
                    <span className="text-2xl mb-2">{block.icon}</span>
                    <span className="font-medium text-sm">{block.label}</span>
                    <span className="text-xs text-gray-500 mt-1">{block.description}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Deadline e Notifiche */}
        <div className="bg-white border rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">Deadline e Notifiche</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data entrata in vigore
              </label>
              <input
                type="date"
                value={form.effectiveDate}
                onChange={(e) => updateForm('effectiveDate', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deadline firma (opzionale)
              </label>
              <input
                type="date"
                value={form.signatureDeadline}
                onChange={(e) => updateForm('signatureDeadline', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.notifications?.onPublish}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  notifications: { ...prev.notifications, onPublish: e.target.checked }
                }))}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm">Invia email alla pubblicazione</span>
            </label>
            
            <div>
              <p className="text-sm text-gray-700 mb-2">
                Reminder automatici (giorni prima della deadline):
              </p>
              <div className="flex gap-2">
                {[7, 3, 1, 0].map(day => (
                  <label key={day} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                    <input
                      type="checkbox"
                      checked={form.notifications?.reminderDays?.includes(day)}
                      onChange={(e) => {
                        const days = form.notifications?.reminderDays || []
                        const newDays = e.target.checked
                          ? [...days, day].sort((a, b) => b - a)
                          : days.filter(d => d !== day)
                        setForm(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, reminderDays: newDays }
                        }))
                      }}
                      className="w-3 h-3 rounded border-gray-300 text-primary-600"
                    />
                    <span className="text-xs">{day === 0 ? 'Giorno stesso' : `${day}gg`}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contenuto */}
        <div className="bg-white border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Contenuto Documento</h2>
            {form.contentSource === 'editor' && (
              <button
                type="button"
                onClick={() => setPreviewMode(!previewMode)}
                className="text-sm text-primary-600 hover:underline flex items-center gap-1"
              >
                <Eye className="h-4 w-4" />
                {previewMode ? 'Editor' : 'Anteprima'}
              </button>
            )}
          </div>
          
          {/* Source Selector */}
          <div className="flex gap-2 mb-4">
            {CONTENT_SOURCES.map(src => (
              <button
                key={src.value}
                type="button"
                onClick={() => updateForm('contentSource', src.value)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  form.contentSource === src.value
                    ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                }`}
              >
                <span>{src.icon}</span>
                <span className="text-sm font-medium">{src.label}</span>
              </button>
            ))}
          </div>
          
          {/* Editor Markdown */}
          {form.contentSource === 'editor' && (
            <div>
              {previewMode ? (
                <div className="prose prose-sm max-w-none border rounded-lg p-4 min-h-[400px] bg-gray-50">
                  <ReactMarkdown>{form.content.body}</ReactMarkdown>
                </div>
              ) : (
                <textarea
                  value={form.content.body}
                  onChange={(e) => updateContent('body', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  rows={20}
                  placeholder="Scrivi il contenuto in formato Markdown...

# Titolo

## Sezione 1

Testo del documento...

- Punto 1
- Punto 2

**Testo in grassetto**, *testo in corsivo*"
                />
              )}
              <p className="text-xs text-gray-500 mt-2">
                Supporta formato Markdown. Usa # per titoli, ** per grassetto, * per corsivo, - per liste.
              </p>
            </div>
          )}
          
          {/* URL Esterno */}
          {form.contentSource === 'external_url' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL Documento
              </label>
              <input
                type="url"
                value={form.content.externalUrl}
                onChange={(e) => updateContent('externalUrl', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="https://www.iubenda.com/terms-and-conditions/..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Inserisci l'URL di un documento esterno (es: Iubenda, Google Docs)
              </p>
              {form.content.externalUrl && (
                <a
                  href={form.content.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline mt-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Apri in nuova scheda
                </a>
              )}
            </div>
          )}
          
          {/* Upload PDF */}
          {form.contentSource === 'pdf' && (
            <div>
              {pdf ? (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <FileText className="h-10 w-10 text-red-500" />
                  <div className="flex-1">
                    <p className="font-medium">{pdf.filename}</p>
                    <p className="text-sm text-gray-500">
                      {(pdf.size / 1024).toFixed(1)} KB • Caricato il {new Date(pdf.uploadedAt).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={pdf.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                    >
                      <Eye className="h-5 w-5" />
                    </a>
                    <button
                      type="button"
                      onClick={handleRemovePdf}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Upload className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">
                    Trascina qui il PDF o clicca per selezionare
                  </p>
                  <p className="text-sm text-gray-500 mb-4">Max 10MB • Solo PDF</p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer">
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Seleziona File
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                      disabled={!isEdit || uploading}
                    />
                  </label>
                  {!isEdit && (
                    <p className="text-xs text-orange-600 mt-3">
                      ⚠️ Salva prima il documento per poter caricare il PDF
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Sommario */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sommario (per preview)
            </label>
            <textarea
              value={form.content.summary}
              onChange={(e) => updateContent('summary', e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={3}
              maxLength={1000}
              placeholder="Breve riassunto del documento mostrato nelle anteprime..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between bg-white border rounded-xl p-4">
          <button
            type="button"
            onClick={() => navigate('/legal-documents')}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Annulla
          </button>
          
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isEdit ? 'Salva Modifiche' : 'Salva Bozza'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

// ============================================
// EXPORT PRINCIPALE
// ============================================

export default function LegalDocumentsPage() {
  return <DocumentsList />
}

export { DocumentForm, DocumentsList }
